'use client'

import { ChangeEvent, useEffect, useState } from 'react'
import { Cell, Radio, Spacer, Text, EmptyState, Validation } from '@vinted/web-ui'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  InputDropdown,
  InputDropdownRenderProps,
} from '@marketplace-web/common-components/input-dropdown-ui'
import { List } from '@marketplace-web/common-components/list-ui'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import {
  getBrands,
  BrandDto,
} from '@marketplace-web/proactive-moderation/proactive-moderation-data'

const DEBOUNCE_DELAY = 350

type Props = {
  selectedBrand?: BrandDto
  setSelectedBrand: (brand: BrandDto) => void
  isSubmitAttempted: boolean
}
const BrandSearchDropdown = ({ selectedBrand, setSelectedBrand, isSubmitAttempted }: Props) => {
  const { fetch: fetchBrands, isLoading: isLoadingBrands, data } = useFetch(getBrands)
  const [searchTerm, setSearchTerm] = useState('')

  const setSearchTermDebounced = useDebounce((value: string) => {
    setSearchTerm(value)
  }, DEBOUNCE_DELAY)

  const handleBrandSearch = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setSearchTermDebounced(target.value)
  }

  const handleListItemClick =
    (brand: BrandDto, inputRenderProps: InputDropdownRenderProps) => () => {
      setSelectedBrand(brand)

      inputRenderProps.closeDropdown()
    }

  const renderBrand = (brand: BrandDto, inputRenderProps: InputDropdownRenderProps) => {
    const isBrandSelected = brand.id === selectedBrand?.id
    const labelId = `radio-label-${brand.id}`

    return (
      <Cell
        id={labelId}
        key={labelId}
        title={<Text as="div" type="title" text={brand.title} id={labelId} />}
        onClick={handleListItemClick(brand, inputRenderProps)}
        suffix={
          <Radio
            name={`radio-${brand.id}`}
            checked={isBrandSelected}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': labelId,
            }}
          />
        }
        clickable
      />
    )
  }

  const renderBrandList = (inputRenderProps: InputDropdownRenderProps) => (
    <div style={{ maxHeight: '320px' }}>
      {data?.brands?.length === 0 && <EmptyState body="No brands found" />}
      <List>{data?.brands?.map(brand => renderBrand(brand, inputRenderProps))}</List>
    </div>
  )

  useEffect(() => {
    fetchBrands({ keyword: undefined })
  }, [fetchBrands])

  useEffect(() => {
    fetchBrands({ keyword: searchTerm })
  }, [fetchBrands, searchTerm])

  return (
    <div className="u-fill-width">
      <Text as="span" text="Brand" type="subtitle" />
      <Spacer />
      <InputDropdown
        name="brand"
        render={renderBrandList}
        isPhone={false}
        textSave=""
        styling="tight"
        placeholder="Search for a brand"
        onChange={handleBrandSearch}
        isLoading={isLoadingBrands}
        value={selectedBrand?.title}
        validation={
          isSubmitAttempted && !selectedBrand ? (
            <Validation text="Please select a brand" theme="warning" />
          ) : undefined
        }
      />
    </div>
  )
}

export default BrandSearchDropdown
